import React, { useCallback, useEffect, useRef, useState } from 'react';
import appNoticeService from '../../services/appNoticeService';
import { useUser } from '../../context/UserContext';
import { useDialog } from '../../context/DialogContext';
import { useAdminShell } from '../admin/shell/AdminShellContext';
import { relTime } from '../admin/shell';
import { SoftField, SoftText } from '../broadcast/ui';
import { StudioCard, ChecksList, RingCta } from '../broadcast/studioUi';
import AudienceEditor, { BLANK_AUDIENCE, targetLabel } from './AudienceEditor';
import ImageField from './ImageField';
import LinkPicker from './LinkPicker';
import { NoticesHero, HeroButton, useNoticesLayout, FilterPills, ListRow, ListEmpty, PreviewCard, counterOf } from './noticesUi';

/**
 * APP NOTICES › PUSH — custom push notifications with an image, sent to a scoped audience
 * (design_handoff_app_notices). A push lands as an in-app notification AND a web / native
 * push (the same pipeline every other notification uses), so the image shows expanded on
 * the lock screen and the tap opens the link.
 *
 * MESSAGE (title, OPENS ON TAP, body) · IMAGE (the shared picker) · WHO SEES IT (the shared
 * AudienceEditor) · BEFORE IT GOES (checks + the ring CTA), beside a lock-screen preview that
 * flips COLLAPSED / EXPANDED. Anyone with `app_notices` · create can draft; only a superadmin
 * can SEND, and a sent push freezes into a record (no edit, no delete). DUPLICATE starts a
 * new draft from a sent one.
 */
const BLANK = { title: '', body: '', linkPath: '', imageUrl: '', imagePath: null, target: { audience: BLANK_AUDIENCE, cities: [] } };
const peopleNoun = (n) => `${Number(n || 0).toLocaleString()} ${n === 1 ? 'PERSON' : 'PEOPLE'}`;
const peopleLower = (n) => `${Number(n || 0).toLocaleString()} ${n === 1 ? 'person' : 'people'}`;

const PUSH_NOTE = {
  collapsed: 'COLLAPSED · what most people see first. The image shows as a small square on the right. Mac and iPhone web push show no image.',
  expanded: 'EXPANDED · after a long-press or pull-down. iOS and Android apps show the image at 2:1. Web push shows it on Android Chrome and Windows only.',
};

/** The lock screen: date, clock, the notification card, OPENS …, the info strip. */
function LockScreen({ push, view, opens }) {
  const now = new Date();
  const date = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).toUpperCase();
  const expanded = view === 'expanded';
  const img = push.imageUrl;
  return (
    <div className="relative flex flex-col items-center overflow-hidden" style={{ width: 390, height: 720, padding: '64px 14px 0', boxSizing: 'border-box', background: 'radial-gradient(90% 60% at 50% 0%, #2B1F4A 0%, #0B0B14 70%)' }}>
      <span className="absolute inset-0" style={{ background: 'rgba(0,0,0,.35)' }} aria-hidden="true" />
      <span className="relative font-mono text-[12px] font-bold tracking-[0.14em] text-white">{date}</span>
      <span className="relative text-white" style={{ fontFamily: "'Helvetica Neue', Arial, sans-serif", fontSize: 84, fontWeight: 600, lineHeight: 1, margin: '4px 0 36px' }}>9:41</span>
      <div className="relative w-full overflow-hidden text-black" style={{ borderRadius: 24, background: 'rgba(245,245,245,.92)' }} data-testid="lock-card">
        <div className="flex" style={{ gap: 12, padding: 14 }}>
          <span className="flex-shrink-0" style={{ width: 38, height: 38, borderRadius: 10, background: '#0A0A0A url(/portal-mark-180.png) center/70% no-repeat' }} aria-hidden="true" />
          <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 3, fontFamily: "'Helvetica Neue', Arial, sans-serif" }}>
            <div className="flex justify-between text-[12px] text-[#6B7280]" style={{ gap: 8 }}><b className="text-black" style={{ letterSpacing: '.04em' }}>PORTAL</b><span>now</span></div>
            <span className="text-[14px] font-bold" style={{ lineHeight: 1.3 }}>{push.title || 'Your title here'}</span>
            <span className="text-[14px] text-[#374151]" style={{ lineHeight: 1.35, display: '-webkit-box', WebkitLineClamp: expanded ? 6 : 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{push.body || 'One or two sentences. Short enough to read on a lock screen.'}</span>
          </div>
          {img && !expanded && <span className="flex-shrink-0 self-center" style={{ width: 44, height: 44, borderRadius: 10, background: `#EDEDED url(${img}) center/cover no-repeat` }} aria-hidden="true" />}
        </div>
        {img && expanded && <span className="block" style={{ aspectRatio: '2 / 1', background: `#EDEDED url(${img}) center/cover no-repeat` }} aria-hidden="true" />}
      </div>
      <span className="relative font-mono text-[10px] tracking-[0.12em] text-white" style={{ marginTop: 12 }} data-testid="push-opens">OPENS {opens}</span>
      <span className="relative font-mono text-[10px] leading-[1.6] tracking-[0.04em] text-white" style={{ marginTop: 'auto', marginBottom: 16, padding: '12px 14px', borderRadius: 16, background: 'rgba(0,0,0,.6)' }}>{PUSH_NOTE[view]}</span>
    </div>
  );
}

export default function PushComposer({ tabs }) {
  const { can, isSuperadmin } = useUser();
  const { showConfirm, showAlert } = useDialog();
  const { toast } = useAdminShell();
  const canCreate = can('app_notices', 'create');
  const canEdit = can('app_notices', 'edit');
  const canDelete = can('app_notices', 'delete');

  const rootRef = useRef(null);
  const finishRef = useRef(null);
  const { wide, narrow, previewCol, cols, twoCol } = useNoticesLayout(rootRef);

  const [pushes, setPushes] = useState(null);
  const [filter, setFilter] = useState('all');
  const [form, setForm] = useState(BLANK);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState('');
  const [pushView, setPushView] = useState('collapsed');
  const [recentUploads, setRecentUploads] = useState([]);
  const [linkRes, setLinkRes] = useState({ ok: true, pending: false, kind: 'PAGE', label: 'Notifications', path: '/notifications' });
  const [reach, setReach] = useState(null);

  const load = useCallback(async () => {
    const r = await appNoticeService.pushes();
    setPushes(r.success ? (r.data || []) : []);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (!flash) return undefined; const t = setTimeout(() => setFlash(''), 3000); return () => clearTimeout(t); }, [flash]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => { setEditingId(null); setForm(BLANK); setError(null); };
  const onResolved = useCallback((res) => setLinkRes(res), []);

  const checks = [
    { key: 'title', label: 'Title', ok: !!form.title.trim() },
    { key: 'body', label: 'Body', ok: !!form.body.trim() },
    { key: 'link', label: 'Link goes somewhere real', ok: !!linkRes.ok && !linkRes.pending },
    { key: 'reach', label: 'Reaches at least one person', ok: (reach || 0) > 0 },
  ];
  const allOk = checks.every((c) => c.ok);

  const payloadOf = (f) => ({
    title: f.title,
    body: f.body,
    linkPath: linkRes.path && f.linkPath ? linkRes.path : (f.linkPath || null),
    // A pasted URL is stored as-is; an upload stores its path so the CDN base can change.
    imagePath: f.imagePath || f.imageUrl || null,
    audience: { scopes: f.target.audience.scopes, clubIds: f.target.audience.clubIds, userIds: f.target.audience.userIds, newWithinDays: f.target.audience.newWithinDays },
    cities: f.target.cities,
  });

  /** Save the open draft → the saved row, or null. */
  const save = async ({ quiet = false } = {}) => {
    if (!allOk || saving) return null;
    setSaving(true);
    setError(null);
    const r = editingId ? await appNoticeService.updatePush(editingId, payloadOf(form)) : await appNoticeService.createPush(payloadOf(form));
    setSaving(false);
    if (r.success) {
      if (!quiet) { setFlash('DRAFT SAVED'); toast('DRAFT SAVED'); reset(); }
      load();
      return r.data;
    }
    setError(r.error || 'Could not save this push');
    return null;
  };

  const edit = (p) => {
    setEditingId(p.id);
    setError(null);
    setFlash('');
    setForm({ title: p.title, body: p.body, linkPath: p.linkPath || '', imageUrl: p.imageUrl || '', imagePath: p.imagePath || null, target: { audience: { ...BLANK_AUDIENCE, ...(p.audience || {}) }, cities: p.cities || [] } });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const duplicate = (p) => { edit(p); setEditingId(null); setFlash('DUPLICATED'); };

  const remove = async (p) => {
    const ok = await showConfirm({ title: 'DELETE DRAFT', message: `Delete the draft "${p.title}"?`, confirmLabel: 'DELETE', variant: 'danger', theme: 'admin' });
    if (!ok) return;
    await appNoticeService.removePush(p.id);
    if (editingId === p.id) reset();
    toast('DRAFT DELETED');
    load();
  };

  const send = async (p, count) => {
    const n = count ?? reach;
    const ok = await showConfirm({
      title: 'SEND THIS PUSH',
      message: `"${p.title}" goes to ${n == null ? targetLabel(p) : peopleLower(n)} right now, as a push and an in-app notification. It cannot be unsent.`,
      confirmLabel: 'SEND NOW',
      variant: 'danger',
      theme: 'admin',
    });
    if (!ok) return false;
    setSendingId(p.id);
    const r = await appNoticeService.sendPush(p.id);
    setSendingId(null);
    if (r.success) {
      setFlash('SENT');
      toast(`SENT TO ${peopleNoun(r.data?.recipientCount || 0)}`);
      load();
      return true;
    }
    await showAlert({ title: 'NOT SENT', message: r.error || 'The send failed before anything went out.', type: 'error', theme: 'admin' });
    return false;
  };

  /** The CTA: a superadmin saves then sends; everyone else saves the draft. */
  const finish = async () => {
    if (!allOk) return;
    if (!isSuperadmin) { await save(); return; }
    const saved = await save({ quiet: true });
    if (!saved) return;
    setEditingId(saved.id);
    const sent = await send(saved, reach);
    if (sent) reset();
  };
  const goFinish = () => finishRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const showComposer = canCreate || (editingId && canEdit);
  const rows = (pushes || []).filter((p) => (filter === 'all' ? true : filter === 'sent' ? p.status === 'sent' : p.status !== 'sent'));
  const total = (pushes || []).length;
  const library = (pushes || []).map((p) => p.imageUrl).filter(Boolean);
  const status = flash || (editingId ? 'EDITING' : 'NEW');
  const opens = `${linkRes.kind} · ${linkRes.label}`.toUpperCase();

  const listCard = (
    <StudioCard title="PUSHES" narrow={narrow} gap={14} testId="pushes-list"
      right={<FilterPills options={[{ value: 'all', label: 'ALL' }, { value: 'drafts', label: 'DRAFTS' }, { value: 'sent', label: 'SENT' }]} value={filter} onChange={setFilter} />}>
      {pushes == null && <ListEmpty>LOADING…</ListEmpty>}
      {rows.map((p) => (
        <ListRow key={p.id} testId={`push-row-${p.id}`} editing={editingId === p.id}
          thumb={p.imageUrl ? { image: p.imageUrl } : { color: '#0A0A0A', text: 'PUSH', ink: '#fff' }}
          title={p.title}
          meta={`${targetLabel(p)} · ${p.sentAt ? `${relTime(p.sentAt).toUpperCase()}${p.sentByName ? ` · ${p.sentByName.toUpperCase()}` : ''}` : relTime(p.createdAt).toUpperCase()}`}
          stats={p.status === 'sent' ? [{ k: 'SENT TO', v: (p.recipientCount || 0).toLocaleString() }] : []}
          pill={p.status === 'sent' ? { label: 'SENT', bg: '#22C55E', fg: '#000' } : p.status === 'sending' ? { label: 'SENDING', bg: '#22ECFB', fg: '#000' } : { label: 'DRAFT', bg: '#2E2E2E', fg: '#fff' }}
          actions={(p.status === 'sent' ? [
            canCreate && { label: 'DUPLICATE', onClick: () => duplicate(p) },
          ] : [
            isSuperadmin && { label: sendingId === p.id ? 'SENDING…' : 'SEND', variant: 'solid', onClick: () => send(p, null), disabled: sendingId === p.id },
            canEdit && { label: 'EDIT', onClick: () => edit(p) },
            canDelete && { label: 'DELETE', variant: 'danger', onClick: () => remove(p) },
          ]).filter(Boolean)}
        />
      ))}
      {pushes != null && rows.length === 0 && <ListEmpty>{filter === 'sent' ? 'NOTHING SENT YET.' : filter === 'drafts' ? 'NO DRAFTS.' : 'NOTHING HERE YET.'}</ListEmpty>}
      <span className="font-mono text-[10px] tracking-[0.12em] text-[#9CA3AF]">{total} {total === 1 ? 'PUSH' : 'PUSHES'}</span>
    </StudioCard>
  );

  return (
    <div ref={rootRef} className="flex flex-col" style={{ gap: 14 }}>
      <NoticesHero status={status} subLine="CUSTOM PUSH · SAME PIPELINE AS EVERY NOTIFICATION" actions={showComposer ? (
        <>
          <HeroButton onClick={reset}>{editingId ? 'CANCEL EDIT' : 'CLEAR'}</HeroButton>
          <HeroButton onClick={() => save()} disabled={!allOk || saving}>SAVE DRAFT</HeroButton>
          <HeroButton variant="white" onClick={goFinish}>{isSuperadmin ? 'REVIEW & SEND' : 'SAVE DRAFT'}</HeroButton>
        </>
      ) : null} />
      {tabs}

      {showComposer ? (
        <div className="grid items-start" style={{ gridTemplateColumns: cols, gap: 14 }}>
          <div className="flex min-w-0 flex-col" style={{ gap: 14 }}>
            <StudioCard title="MESSAGE" note="LANDS AS A PUSH AND IN NOTIFICATIONS" narrow={narrow} gap={16}>
              <div className="grid" style={{ gridTemplateColumns: twoCol, gap: 14 }}>
                <SoftField label="TITLE" required right={counterOf(form.title, 80).text} rightColor={counterOf(form.title, 80).color} htmlFor="push-title">
                  <SoftText id="push-title" maxLength={80} value={form.title} onChange={(v) => set('title', v)} placeholder="Saturday is almost sold out." />
                </SoftField>
                <div className="flex min-w-0 flex-col" style={{ gap: 8 }}>
                  <span className="flex justify-between font-mono text-[10px] font-bold tracking-[0.16em] text-[#6B7280]" style={{ gap: 8 }}><span className="whitespace-nowrap">OPENS ON TAP</span><span className="whitespace-nowrap text-[#9CA3AF]">BLANK = NOTIFICATIONS</span></span>
                  <LinkPicker id="push-link" mode="push" value={form.linkPath} onChange={(v) => set('linkPath', v)} onResolved={onResolved} />
                </div>
              </div>
              <SoftField label="BODY" required right={counterOf(form.body, 200).text} rightColor={counterOf(form.body, 200).color} htmlFor="push-body">
                <SoftText id="push-body" rows={3} maxLength={200} value={form.body} onChange={(v) => set('body', v)} placeholder="Short enough to read on a lock screen." />
              </SoftField>
            </StudioCard>

            <StudioCard title="IMAGE" note="OPTIONAL · SHOWS WHEN EXPANDED" narrow={narrow} gap={14}>
              <ImageField optional value={form.imageUrl} onChange={(url, path) => { set('imageUrl', url); set('imagePath', path); }} library={library} recent={recentUploads}
                onUploaded={(url) => setRecentUploads((r) => [url, ...r.filter((x) => x !== url)])}
                help="JPG or PNG, 2:1 works best. Shows large when the push is expanded and as a thumbnail when collapsed." />
            </StudioCard>

            <AudienceEditor value={form.target} onChange={(t) => set('target', t)} onReach={setReach} narrow={narrow} />

            <StudioCard title="BEFORE IT GOES" note={isSuperadmin ? 'SENT PUSHES CANNOT BE EDITED' : 'DRAFTS ONLY'} narrow={narrow} innerRef={finishRef} testId="finish-card">
              <ChecksList checks={checks} />
              {!isSuperadmin && <span className="font-mono text-[11px] leading-[1.6] text-[#4B5563]" style={{ padding: '12px 16px', borderRadius: 16, background: '#F5F5F5' }}>You can save drafts. A superadmin sends them.</span>}
              {error && <div className="font-mono text-[11px] leading-[1.6] text-[#B91C1C]" style={{ padding: '10px 14px', borderRadius: 14, background: '#FEF2F2' }} role="alert">{error}</div>}
              <RingCta ready={allOk && !saving && !sendingId} onClick={finish} testId="finish-push">
                {saving ? 'SAVING…' : sendingId ? 'SENDING…' : isSuperadmin ? `SEND NOW TO ${peopleNoun(reach)}` : 'SAVE DRAFT'}
              </RingCta>
            </StudioCard>

            {wide && listCard}
          </div>

          <PreviewCard title="PREVIEW · LOCK SCREEN" views={[{ value: 'collapsed', label: 'COLLAPSED' }, { value: 'expanded', label: 'EXPANDED' }]} view={pushView} onView={setPushView} previewCol={previewCol} canW={390} sticky={wide}>
            <LockScreen push={form} view={pushView} opens={opens} />
          </PreviewCard>
          {!wide && listCard}
        </div>
      ) : listCard}
    </div>
  );
}
